// src/context/Adviser/Utils/SaveAdviserLicenseCertificates.js

import {
  addDoc,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { auth, db } from "../../../backend/firebaseConfig";

export const getAdviserLicenseCertificatePaths = (adviserId) => {
  if (!adviserId) {
    throw new Error("Adviser id is required.");
  }

  const licenseCertificatesRef = doc(
    db,
    "Authorized Members",
    "Advisers",
    "Data",
    adviserId,
    "License Certificates",
    "Data",
  );

  return {
    licenseCertificatesRef,
    editLogsRef: collection(licenseCertificatesRef, "Edit Logs"),
  };
};

const cleanObject = (value) => {
  if (!value || typeof value !== "object") return {};

  return Object.entries(value).reduce((acc, [key, item]) => {
    if (item && typeof item === "object" && !Array.isArray(item)) {
      acc[key] = cleanObject(item);
      return acc;
    }

    acc[key] = typeof item === "string" ? item.trim() : (item ?? "");
    return acc;
  }, {});
};

const flattenObject = (value = {}, prefix = "") => {
  return Object.entries(value || {}).reduce((acc, [key, item]) => {
    const path = prefix ? `${prefix}.${key}` : key;

    if (
      item &&
      typeof item === "object" &&
      !Array.isArray(item) &&
      !item?.seconds &&
      !item?.nanoseconds
    ) {
      return {
        ...acc,
        ...flattenObject(item, path),
      };
    }

    acc[path] = item ?? "";
    return acc;
  }, {});
};

const normaliseCompareValue = (value) => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.join(", ");
  return String(value).trim();
};

const getChangedFields = (before = {}, after = {}) => {
  const beforeFlat = flattenObject(before);
  const afterFlat = flattenObject(after);

 const keys = Object.keys(afterFlat).filter((key) => {
   return ![
     "adviserEmail",
     "adviserName",
     "adviserId",
     "updatedAt",
     "updatedBy",
     "updatedByName",
     "createdAt",
     "createdBy",
     "followUpSummary",
     "bulkImport",
   ].some((ignoredKey) => key.startsWith(ignoredKey));
 });

  return keys
    .map((fieldPath) => {
      const previousValue = normaliseCompareValue(beforeFlat[fieldPath]);
      const newValue = normaliseCompareValue(afterFlat[fieldPath]);

      if (previousValue === newValue) return null;

      return {
        fieldPath,
        previousValue,
        newValue,
      };
    })
    .filter(Boolean);
};

const setNestedValue = (target, path = "", value) => {
  const parts = path.split(".").filter(Boolean);

  if (!parts.length) return target;

  let current = target;

  parts.forEach((part, index) => {
    if (index === parts.length - 1) {
      current[part] = value;
      return;
    }

    current[part] = current[part] || {};
    current = current[part];
  });

  return target;
};

const buildFieldUpdateSummary = ({
  changedFields = [],
  updatedAt,
  updatedBy,
  updatedByName,
}) => {
  const summary = {};

  changedFields.forEach((change) => {
    setNestedValue(summary, change.fieldPath, {
      updatedAt,
      updatedBy,
      updatedByName,
      previousValue: change.previousValue || "",
      newValue: change.newValue || "",
    });
  });

  return summary;
};

const saveAdviserLicenseCertificatesForId = async (adviserId, values) => {
  const { licenseCertificatesRef, editLogsRef } =
    getAdviserLicenseCertificatePaths(adviserId);

  const cleanedValues = cleanObject(values);

  const currentSnap = await getDoc(licenseCertificatesRef);
  const previousValues = currentSnap.exists() ? currentSnap.data() || {} : {};

  const updatedBy = auth.currentUser?.email || "";
  const updatedByName = auth.currentUser?.displayName || updatedBy || "Unknown";
  let changedFields = getChangedFields(previousValues, cleanedValues);

  if (changedFields.some((change) => change.fieldPath.startsWith("cpd."))) {
    Object.values(cleanedValues.cpd || {}).forEach((record) => {
      record.updatedBy = updatedBy;
      record.updatedByName = updatedByName;
    });
    changedFields = getChangedFields(previousValues, cleanedValues);
  }

  const updatedAt = new Date().toISOString();

  const fieldUpdateSummary = buildFieldUpdateSummary({
    changedFields,
    updatedAt,
    updatedBy,
    updatedByName,
  });

  await setDoc(
    licenseCertificatesRef,
    {
      ...cleanedValues,
      fieldUpdateSummary,
      updatedAt,
      updatedBy,
      updatedByName,
    },
    { merge: true },
  );

  if (changedFields.length > 0) {
    await addDoc(editLogsRef, {
      adviserId,
      updatedAt: serverTimestamp(),
      updatedBy,
      updatedByName,
      changeCount: changedFields.length,
      changes: changedFields,
    });
  }

  return cleanedValues;
};

export const saveAdviserLicenseCertificates = async (
  adviserId,
  values,
  alternateAdviserId = "",
) => {
  const adviserIds = [...new Set([adviserId, alternateAdviserId])].filter(
    (value) => value && value !== "-",
  );

  if (!adviserIds.length) {
    throw new Error("Adviser ID or email is required.");
  }

  await Promise.all(
    adviserIds.map((currentId) =>
      saveAdviserLicenseCertificatesForId(currentId, values),
    ),
  );

  return cleanObject(values);
};
